import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideCheckNoChangesConfig,
  provideZonelessChangeDetection,
} from '@angular/core';
import {
  RouteReuseStrategy,
  provideRouter,
  withEnabledBlockingInitialNavigation,
  withInMemoryScrolling,
  withRouterConfig,
} from '@angular/router';
import { A11yModule } from '@angular/cdk/a11y';
import { ServiceWorkerModule, SwRegistrationOptions } from '@angular/service-worker';
import { IonicModule, IonicRouteStrategy } from '@ionic/angular';

import { provideCore } from '@falcon/app/core/core.providers';
import {
  RollbarErrorHandler,
  RollbarService,
  rollbarFactory,
} from '@falcon/app/core/rollbar/rollbar';
import { provideSharedDefaults } from '@falcon/app/shared/shared.providers';
import { provideDialog } from '@falcon/app/shared/services/dialog/dialog.providers';
import { providePhone } from '@falcon/app/shared/services/phone/phone.providers';
import { provideSnackBar } from '@falcon/app/shared/services/snack-bar/snack-bar.providers';
import {
  featureFlagFactoryInit,
  FeatureFlagService,
} from '@falcon/app/shared/services/feature-flag/feature-flag.service';
import { PlatformDetector } from '@falcon/app/shared/utils/platform-detector';
import { ROUTES } from './app.routes';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    // Dev mode only (a no-op in optimized builds): reports bindings that changed without notifying change
    // detection, i.e. views that would stay stale without zone.js
    provideCheckNoChangesConfig({ exhaustive: true, interval: 1000 }),
    provideRouter(
      ROUTES,
      withEnabledBlockingInitialNavigation(),
      withRouterConfig({ paramsInheritanceStrategy: 'always' }),
      withInMemoryScrolling({ anchorScrolling: 'enabled', scrollPositionRestoration: 'enabled' })
    ),
    importProvidersFrom(ServiceWorkerModule.register('ngsw-worker.js'), IonicModule.forRoot()),
    provideCore(),
    // Formerly reached the root injector through CoreModule > InterceptorsModule > DialogModule >
    // SharedModule (A11yModule's constructor applies the high-contrast body classes)
    provideSharedDefaults(),
    importProvidersFrom(A11yModule),
    providePhone(),
    provideDialog(),
    provideSnackBar(),
    provideAppInitializer(() => featureFlagFactoryInit(inject(FeatureFlagService))()),
    {
      provide: SwRegistrationOptions,
      useFactory: (): SwRegistrationOptions => ({
        enabled: !PlatformDetector.isNative && window.location.port !== '1443',
      }),
    },
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    { provide: ErrorHandler, useClass: RollbarErrorHandler },
    { provide: RollbarService, useFactory: rollbarFactory },
  ],
};
